<template>
    <div class="form form__date">
        <label
            v-if="label"
            :for="name | toKebabCase"
            class="form__date_title"
        >{{label | toCapitalize}}<span class="asterisk" v-if="required">*</span></label>
        <div class="form__date_field">
            <form-text
                :disabled="disabled"
                :name="name"
                :class="errors.first(`${name}-date--hidden`) ? 'invalid' : ''"
                :label="''"
                :value="outputValue"
                ref="textinput"
                :prevent="true"
                @input="emitInput"
                @emit-focus="openDatepicker"
            />
            <form-text
                class="form__date_field_form-hidden"
                :value="value"
                :name="`${name}-date--hidden`"
                :v-validate="vValidate"
                :v-validate-custom-error="vValidateCustomError"
                :data-vv-as="dataVvAs ? dataVvAs : label.toLowerCase()"
                :label="''"
            />
            <template v-if="datepickerActive">
                <component-datepicker
                    class="form__date_field_datepicker"
                    ref="datepicker"
                    :value="betweenMode ? highlighted.to : value"
                    :close-button-active="true"
                    :highlighted="highlighted"
                    @datepicker-input="emitInput"
                    @close-datepicker="closeDatepicker"
                />
            </template>
            <template v-if="value && !disabled">
                <component-button
                    :theme="''"
                    :icon-raw="true"
                    :icon="true"
                    :icon-name="'x'"
                    @click="emitInput('')"
                    :name="'form-date-field-form-x'"
                />
            </template>
            <template v-else>
                <v-icon
                    class="form__date_icon--noevent"
                    name="calendar"
                ></v-icon>
            </template>
        </div>
        <template v-if="$slots.default">
            <div class="form__date_hint">
                <template v-if="hintButton">
                    <component-button
                        :name="'form-date-hint-button'"
                        :text="true"
                        :size="'tiny'"
                        @click="emitHint"
                    >
                        <slot></slot>
                    </component-button>
                </template>
                <template v-else>
                    <slot></slot>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentDatepicker from '@/components/default/shared/ComponentDatepicker'
import FormText from '@/components/default/forms/FormText'
import { toKebabCase } from '@/functions'

export default {
  name: 'form-date',
  components: {
    FormText,
    ComponentDatepicker,
    ComponentButton
  },
  data () {
    return {
      datepickerActive: false,
      betweenStep: 0
    }
  },
  props: {
    valueFrom: {
      type: [String, Date]
    },
    valueTo: {
      type: [String, Date]
    },
    betweenMode: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true,
      default: 'Label'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Date]
    },
    hintButton: {
      type: Boolean,
      default: false
    },
    //
    dataVvAs: {
      type: String,
      default: ''
    },
    vValidate: {
      type: [String, Object]
    },
    vValidateCustomError: {
      type: String
    }
  },
  computed: {
    highlighted () {
      let dates = {}
      if (this.valueFrom && this.valueTo) {
        dates.from = new Date(this.valueFrom)
        dates.to = new Date(this.valueTo)
      }
      return dates
    },
    newValue: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
      }
    },
    outputValue () {
      if (this.betweenMode) {
        return `${this.valueFrom} - ${this.valueTo}`
      } else {
        return this.value
      }
    }
  },
  methods: {
    openDatepicker () {
      this.emitFocus()
      this.datepickerActive = true
      if (this.betweenMode) {
        this.betweenStep = 0
      }
    },
    closeDatepicker () {
      this.datepickerActive = !this.datepickerActive
    },
    autoCloseDatepicker (event) {
      const path = event.path || event.composedPath()

      if (!path) return
      let closeDate = true
      for (let i = 0; i < path.length; i++) {
        if (!path[i].className || path[i].className.constructor !== String) continue
        if (
          path[i].className.includes('form__date')
        ) {
          closeDate = false
        }
      }
      if (closeDate) {
        this.datepickerActive = false
      }
    },
    emitFocus () {
      const field = this.$validator.errors.items.find(obj => obj.field === toKebabCase(this.name))
      this.$validator.pause()
      if (field) {
        this.$validator.reset(field)
      }
      this.$emit('emit-focus')
    },
    emitBlur () {
      const field = this.$validator.errors.items.find(obj => obj.field === toKebabCase(this.name))
      this.$validator.resume()
      if (field) {
        this.$validator.reset(field)
      }
      this.$emit('emit-blur')
    },
    emitHint () {
      this.$emit('emit-hint')
    },
    emitInput (date) {
      this.emitBlur()

      const isDate = date.constructor === Date

      if (date && (isDate || date.match(/^\d{4}[-](0?[1-9]|1[012])[-](0?[1-9]|[12][0-9]|3[01])$/))) {
        const dateValue = isDate ? date : new Date(date)
        const formattedDate = isDate ? `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}` : date

        if (this.betweenMode) {
          if (this.betweenStep === 0) {
            this.betweenStep = 1
            this.$emit(`input-from`, formattedDate)
            if (dateValue > new Date(this.valueTo)) {
              this.$emit(`input-to`, formattedDate)
            }
          } else {
            this.betweenStep = 0
            this.$emit(`input-to`, dateValue > new Date(this.valueFrom) ? formattedDate : this.valueFrom)
            this.closeDatepicker()
          }
        } else {
          this.$emit('input', formattedDate)
        }
      } else {
        this.$emit('input', '')
      }
    }
  },
  mounted () {
    const overlay = document.getElementById('app')
    overlay.addEventListener('click', this.autoCloseDatepicker)
  },
  beforeDestroy () {
    const overlay = document.getElementById('app')
    overlay.removeEventListener('click', this.autoCloseDatepicker)
  },
  updateValueOther (value) {
    let objectValues = JSON.parse(JSON.stringify(this.objectValue))
    let newValue = JSON.parse(JSON.stringify(this.objectValue))
    if (this.multiple) {
      if (value && !objectValues.some(obj => obj.value === value)) {
        newValue.push({ label: '', value: value })
      }
    } else {
      newValue.push({ label: '', value: value })
    }
    this.otherFieldValue = value
    this.$emit('input', newValue)
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/forms/formdate";
</style>
